/**
 * ConversationLog: Interview 案B の chat-bubble 形式で会話記録を描画。
 * - 学生(question): 右寄せ、オレンジ系バブル
 * - 相手(response): 左寄せ、グレー/青系バブル
 * タイムスタンプと件数は表示しない。
 */
import { memo, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { ConversationEntry } from "../types";

interface ConversationLogProps {
  conversations: ConversationEntry[];
  currentInterimText?: string;
  counterpartLabel?: string; // 相手側のラベル（例: "患者" / "歯科医" / デフォルト "アバター"）
  counterpartBubbleBg?: string; // 相手側のバブル背景色
  /** バブル内テキストのカスタムレンダラ（評価ページで HighlightedConversation を使う用） */
  renderText?: (entry: ConversationEntry) => ReactNode;
  /** バブル内の補助操作（管理者向け録音再生など） */
  renderActions?: (entry: ConversationEntry) => ReactNode;
  /** 空状態のプレースホルダ（デフォルト: 「マイクをタップ · Enter で開始」） */
  emptyPlaceholder?: ReactNode;
  /** 外側 div の height。デフォルト "100%" */
  height?: number | string;
  /** 相手側の発言の横に出すアイコン（評価ページで患者・歯科医の顔を出す用） */
  counterpartIcon?: ReactNode;
  /** 学生側の発言の横に出すアイコン */
  studentIcon?: ReactNode;
}

const STUDENT_BUBBLE_BG = "#FFEDD5"; // オレンジ系
const PATIENT_BUBBLE_BG = "#F3F4F6"; // グレー

interface ConversationItemProps {
  entry: ConversationEntry;
  index: number;
  counterpartLabel: string;
  counterpartBubbleBg: string;
  renderText?: (entry: ConversationEntry) => ReactNode;
  renderActions?: (entry: ConversationEntry) => ReactNode;
  counterpartIcon?: ReactNode;
  studentIcon?: ReactNode;
}

const ConversationItem = memo(function ConversationItem({
  entry,
  index,
  counterpartLabel,
  counterpartBubbleBg,
  renderText,
  renderActions,
  counterpartIcon,
  studentIcon,
}: ConversationItemProps) {
  const isStudent = entry.type === "question";
  const icon = isStudent ? studentIcon : counterpartIcon;
  const body = (
    <div
      key={entry.id || index}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: isStudent ? "flex-end" : "flex-start",
        marginBottom: icon ? 0 : 12,
        flex: icon ? "1 1 auto" : undefined,
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: "#A8A29E",
          fontWeight: 600,
          marginBottom: 4,
          padding: "0 4px",
        }}
      >
        {isStudent
          ? entry.isInterim
            ? "あなた（認識中...）"
            : "あなた"
          : counterpartLabel}
      </div>
      <div
        style={{
          maxWidth: "85%",
          padding: "10px 14px",
          borderRadius: 14,
          borderBottomRightRadius: isStudent ? 4 : 14,
          borderBottomLeftRadius: isStudent ? 14 : 4,
          background: isStudent ? STUDENT_BUBBLE_BG : counterpartBubbleBg,
          color: "#18181B",
          fontSize: 14,
          lineHeight: 1.6,
          fontStyle: entry.isInterim ? "italic" : "normal",
          opacity: entry.isInterim ? 0.75 : 1,
          wordBreak: "break-word",
        }}
      >
        {renderText ? renderText(entry) : entry.text}
        {renderActions?.(entry)}
        {entry.isInterim && (
          <span
            style={{
              marginLeft: 6,
              color: "#2563EB",
              animation: "pulse 1.4s ease-in-out infinite",
            }}
          >
            ▋
          </span>
        )}
      </div>
    </div>
  );
  if (!icon) return body;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: isStudent ? "row-reverse" : "row",
        alignItems: "flex-start",
        gap: 8,
        marginBottom: 12,
      }}
    >
      <div style={{ flex: "0 0 auto", marginTop: 2 }}>{icon}</div>
      {body}
    </div>
  );
});

function ConversationLog({
  conversations,
  currentInterimText = "",
  counterpartLabel = "アバター",
  counterpartBubbleBg = PATIENT_BUBBLE_BG,
  renderText,
  renderActions,
  emptyPlaceholder,
  height = "100%",
  counterpartIcon,
  studentIcon,
}: ConversationLogProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const interimText = currentInterimText.trim();
  const interimEntry: ConversationEntry | null = interimText
    ? {
        id: "__current_stt_interim__",
        type: "question",
        text: interimText,
        timestamp: new Date(0),
        isInterim: true,
      }
    : null;

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 180;
    if (!isNearBottom) return;

    const frame = window.requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [conversations, interimText]);

  return (
    <div
      ref={scrollContainerRef}
      style={{
        height,
        overflowY: "auto",
        overflowX: "hidden",
        padding: "16px 16px 12px",
        fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
      }}
    >
      {conversations.length === 0 && !interimEntry ? (
        <div
          style={{
            textAlign: "center",
            padding: "32px 0",
            color: "#71717A",
            fontSize: 13,
          }}
        >
          {emptyPlaceholder ?? "マイクをタップ · Enter で開始"}
        </div>
      ) : (
        <>
          {conversations.map((entry, index) => (
            <ConversationItem
              key={entry.id || index}
              entry={entry}
              index={index}
              counterpartLabel={counterpartLabel}
              counterpartBubbleBg={counterpartBubbleBg}
              renderText={renderText}
              renderActions={renderActions}
              counterpartIcon={counterpartIcon}
              studentIcon={studentIcon}
            />
          ))}
          {interimEntry && (
            <ConversationItem
              key={interimEntry.id}
              entry={interimEntry}
              index={conversations.length}
              counterpartLabel={counterpartLabel}
              counterpartBubbleBg={counterpartBubbleBg}
              renderText={renderText}
              renderActions={renderActions}
              counterpartIcon={counterpartIcon}
              studentIcon={studentIcon}
            />
          )}
        </>
      )}
    </div>
  );
}

export default memo(ConversationLog);
